import { useState, useEffect } from "react";
import { RecurrenceInput } from "@/components/shared/RecurrenceInput";

/** Live elapsed-time display for active focus items */
function ElapsedTime({ startedAt, durationSeconds }: { startedAt: string; durationSeconds: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  const elapsed = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  const total = Math.round(durationSeconds / 60);
  return (
    <p className="text-xs text-muted-foreground mt-1">
      ⏱️ {m}:{String(s).padStart(2, "0")} / {total} min
    </p>
  );
}
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Plus, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useCategorySuggestion } from "@/hooks/useCategorySuggestion";
import { useParentTaskCoach } from "@/hooks/useParentTaskCoach";
import { isTooVague } from "@/hooks/useTaskCoach";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type TaskItem = any;

const TASK_CATEGORIES = [
  { value: "Akademisk mestring", emoji: "📚" },
  { value: "Fysisk helse", emoji: "🏃" },
  { value: "Egenomsorg", emoji: "🧼" },
  { value: "Kreativ utfoldelse", emoji: "🎨" },
  { value: "Sosial kompetanse", emoji: "🤝" },
  { value: "Ansvar hjemme", emoji: "🏠" },
  { value: "Selvstendighet", emoji: "💪" },
  { value: "Personlig utvikling", emoji: "🌱" },
  { value: "Annet", emoji: "✨" },
];

function getCategoryEmoji(cat: string | null): string {
  return TASK_CATEGORIES.find((c) => c.value === cat)?.emoji ?? "✨";
}

interface TaskItemDetailSheetProps {
  item: TaskItem | null;
  open: boolean;
  onClose: () => void;
  onEdit: (itemId: string, updates: { title?: string; durationSeconds?: number; description?: string }) => void;
  onDelete: (itemId: string) => void;
  onComplete: (itemId: string) => void;
  onReset?: (itemId: string) => void;
  onMove?: (itemId: string, newDate: string) => void;
  onCreate?: (title: string, durationSeconds: number, category?: string, description?: string) => void;
  onCreateRecurring?: (data: {
    title: string;
    description: string;
    duration: number;
    category: string;
    recurrenceType: "daily" | "weekly";
    weeklyDays: number[];
    startDate: string;
    endDate: string | null;
  }) => void;
  isParent: boolean;
  createMode?: boolean;
  creatorName?: string | null;
  needsApproval?: boolean;
  onApprove?: (itemId: string) => void;
  onCategoryChange?: (itemId: string, category: string) => void;
  familyId?: string | null;
  childId?: string | null;
}

const statusLabel: Record<string, string> = {
  pending: "Ikke startet",
  active: "Pågår",
  done: "Ferdig",
};

export function TaskItemDetailSheet({
  item,
  open,
  onClose,
  onEdit,
  onDelete,
  onComplete,
  onReset,
  onMove,
  onCreate,
  onCreateRecurring,
  isParent,
  createMode = false,
  creatorName,
  needsApproval = false,
  onApprove,
  onCategoryChange,
  familyId,
  childId,
}: TaskItemDetailSheetProps) {
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDuration, setEditDuration] = useState(600);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  // Recurrence state for create mode
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState<"daily" | "weekly">("daily");
  const [weeklyDays, setWeeklyDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [hasEndDate, setHasEndDate] = useState(false);
  const [endDate, setEndDate] = useState("");
  const categorySuggestion = useCategorySuggestion(familyId ?? null);
  const parentCoach = useParentTaskCoach(childId ?? undefined, familyId ?? null);

  // Sync state when item or createMode changes
  useEffect(() => {
    if (createMode) {
      setEditTitle("");
      setEditDescription("");
      setEditDuration(600);
      setSelectedCategory("Annet");
      setIsEditing(false);
      setRecurrenceEnabled(false);
      setRecurrenceType("daily");
      setWeeklyDays([1, 2, 3, 4, 5]);
      setStartDate(new Date().toISOString().slice(0, 10));
      setHasEndDate(false);
      setEndDate("");
      categorySuggestion.reset();
      parentCoach.reset();
    } else if (item) {
      setEditTitle(item.title);
      setEditDescription(item.description || "");
      setEditDuration(item.duration_seconds);
      setSelectedCategory(item.category);
      setIsEditing(false);
    }
  }, [item, createMode, open]);

  // Trigger category suggestion on title change in create mode
  useEffect(() => {
    if (createMode && editTitle.trim().length >= 2) {
      categorySuggestion.suggest(editTitle);
    }
  }, [editTitle, createMode]);

  // Trigger parent coaching on title change in create mode (parent only)
  useEffect(() => {
    if (createMode && isParent && editTitle.trim().length >= 2) {
      parentCoach.check(editTitle);
    }
  }, [editTitle, createMode, isParent]);

  // Auto-set category from suggestion (only if user hasn't manually chosen)
  useEffect(() => {
    if (createMode && categorySuggestion.suggestion && selectedCategory === "Annet") {
      setSelectedCategory(categorySuggestion.suggestion.category);
    }
  }, [categorySuggestion.suggestion, createMode]);

  if (!open) return null;
  if (!createMode && !item) return null;

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setIsEditing(false);
      categorySuggestion.reset();
      onClose();
    }
  }

  function handleCreate() {
    if (!editTitle.trim() || !onCreate) return;
    onCreate(editTitle.trim(), editDuration, selectedCategory || undefined, editDescription.trim() || undefined);
    categorySuggestion.reset();
  }

  function startEditing() {
    if (!item) return;
    setEditTitle(item.title);
    setEditDescription(item.description || "");
    setEditDuration(item.duration_seconds);
    setIsEditing(true);
  }

  function saveEdit() {
    if (!item) return;
    const updates: { title?: string; durationSeconds?: number; description?: string } = {};
    if (editTitle.trim() && editTitle.trim() !== item.title) updates.title = editTitle.trim();
    if (editDuration !== item.duration_seconds) updates.durationSeconds = editDuration;
    const newDesc = editDescription.trim();
    if (newDesc !== (item.description || "")) updates.description = newDesc;
    if (Object.keys(updates).length > 0) {
      onEdit(item.id, updates);
    }
    setIsEditing(false);
  }

  const itemCategory = item?.category ?? "Annet";
  const givesPoint = itemCategory !== "Annet";

  // Create mode
  if (createMode) {
    const suggestedCat = selectedCategory || categorySuggestion.suggestion?.category;
    const catGivesPoint = suggestedCat && suggestedCat !== "Annet";
    const showLowConfidence = categorySuggestion.suggestion?.category === "Annet" && categorySuggestion.suggestion?.confidence !== undefined && categorySuggestion.suggestion.confidence < 0.65;

    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle>Ny oppgave</SheetTitle>
            <SheetDescription>Fyll inn detaljer for oppgaven</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Tittel</p>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Hva skal barnet gjøre?"
                
              />
            </div>

            {/* Parent AI coaching suggestions */}
            {isParent && !parentCoach.coachingDisabled && isTooVague(editTitle) && editTitle.trim().length >= 2 && (
              <div className="space-y-1.5">
                {parentCoach.isLoading && (
                  <p className="text-xs text-muted-foreground animate-pulse">💡 Henter forslag …</p>
                )}
                {parentCoach.coach && parentCoach.coach.rewriteSuggestions.length > 0 && (
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-2.5 space-y-1.5">
                    {parentCoach.coach.hint && (
                      <p className="text-xs text-muted-foreground">💡 {parentCoach.coach.hint}</p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {parentCoach.coach.rewriteSuggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                          onClick={() => { setEditTitle(s); parentCoach.reset(); }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1">Beskrivelse (valgfritt)</p>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Legg til leksebeskrivelse, instruksjoner, lenker…"
                rows={2}
                className="text-sm"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground mb-1">Kategori</p>
              <Select
                value={selectedCategory || suggestedCat || "Annet"}
                onValueChange={(v) => setSelectedCategory(v)}
              >
                <SelectTrigger className="h-9 text-sm rounded-lg w-auto min-w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.emoji} {c.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categorySuggestion.isLoading && (
                <p className="text-xs text-muted-foreground animate-pulse">Foreslår kategori…</p>
              )}
              {categorySuggestion.suggestion && (
                <p className="text-xs text-muted-foreground">
                  {catGivesPoint
                    ? `Gir 1 poeng · ${categorySuggestion.suggestion.reason}`
                    : "Gir ikke poeng"}
                  {" · "}
                  <span className="text-muted-foreground/70">Du kan endre om du vil</span>
                </p>
              )}
              {showLowConfidence && (
                <p className="text-xs text-amber-600">
                  Gjør oppgaven mer konkret for å få riktig kategori.
                </p>
              )}
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">Varighet</p>
              <Select value={String(editDuration)} onValueChange={(v) => setEditDuration(Number(v))}>
                <SelectTrigger className="h-9 text-sm rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="300">5 min</SelectItem>
                  <SelectItem value="600">10 min</SelectItem>
                  <SelectItem value="900">15 min</SelectItem>
                  <SelectItem value="1200">20 min</SelectItem>
                  <SelectItem value="1800">30 min</SelectItem>
                  <SelectItem value="2700">45 min</SelectItem>
                  <SelectItem value="3600">60 min</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Recurrence toggle */}
            {onCreateRecurring && (
              <RecurrenceInput
                enabled={recurrenceEnabled}
                onEnabledChange={setRecurrenceEnabled}
                type={recurrenceType}
                onTypeChange={setRecurrenceType}
                weeklyDays={weeklyDays}
                onWeeklyDaysChange={setWeeklyDays}
                showDateRange={recurrenceEnabled}
                startDate={startDate}
                onStartDateChange={setStartDate}
                hasEndDate={hasEndDate}
                onHasEndDateChange={setHasEndDate}
                endDate={endDate}
                onEndDateChange={setEndDate}
              />
            )}

            <Button
              className="w-full"
              onClick={() => {
                if (recurrenceEnabled && onCreateRecurring) {
                  onCreateRecurring({
                    title: editTitle.trim(),
                    description: editDescription.trim(),
                    duration: editDuration,
                    category: selectedCategory || suggestedCat || "Annet",
                    recurrenceType,
                    weeklyDays,
                    startDate,
                    endDate: hasEndDate && endDate ? endDate : null,
                  });
                } else {
                  handleCreate();
                }
              }}
              disabled={!editTitle.trim() || (recurrenceEnabled && recurrenceType === "weekly" && weeklyDays.length === 0)}
            >
              {recurrenceEnabled ? (
                <><Repeat className="h-4 w-4" /> Opprett gjentagende oppgave</>
              ) : (
                <><Plus className="h-4 w-4" /> Legg til oppgave</>
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Detail/edit mode for existing item
  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader className="text-left sticky top-0 bg-background z-10 pb-2">
          <SheetTitle>{isParent ? "Oppgave" : "Min oppgave"}</SheetTitle>
          <SheetDescription>{isParent ? "Detaljer og handlinger" : "Se hvordan det går"}</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Tittel</p>
            {isEditing ? (
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                
              />
            ) : (
              <p className="font-medium text-foreground">{item!.title}</p>
            )}
          </div>

          {/* Description */}
          {(isEditing || item!.description) && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Beskrivelse</p>
              {isEditing ? (
                <Textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Leksebeskrivelse, instruksjoner…"
                  rows={2}
                  className="text-sm"
                />
              ) : (
                <p className="text-sm bg-muted rounded-lg p-2 whitespace-pre-wrap">{item!.description}</p>
              )}
            </div>
          )}

          {/* Status */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              {isParent ? "Status" : "Hvor langt har jeg kommet?"}
            </p>
            <p className="text-sm font-medium">{statusLabel[item!.status] ?? item!.status}</p>
            {item!.status === "active" && item!.started_at && (
              <ElapsedTime startedAt={item!.started_at} durationSeconds={item!.duration_seconds} />
            )}
          </div>

          {/* Duration */}
          {isParent && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Varighet</p>
              {isEditing ? (
                <Select value={String(editDuration)} onValueChange={(v) => setEditDuration(Number(v))}>
                  <SelectTrigger className="h-9 text-sm rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="300">5 min</SelectItem>
                    <SelectItem value="600">10 min</SelectItem>
                    <SelectItem value="900">15 min</SelectItem>
                    <SelectItem value="1200">20 min</SelectItem>
                    <SelectItem value="1800">30 min</SelectItem>
                    <SelectItem value="2700">45 min</SelectItem>
                    <SelectItem value="3600">60 min</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm font-medium">{Math.round(item!.duration_seconds / 60)} min</p>
              )}
            </div>
          )}

          {/* Points info */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Poeng</p>
            <p className="text-sm font-medium">
              {givesPoint ? "1 poeng ⭐" : "0 poeng (Annet)"}
            </p>
          </div>

          {/* Created */}
          <div className="flex gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Opprettet</p>
              <p className="text-sm">
                {new Date(item!.created_at).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
            {creatorName && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Opprettet av</p>
                <p className="text-sm font-medium">{creatorName}</p>
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Kategori</p>
            {isParent && onCategoryChange ? (
              <Select
                value={item!.category ?? "Annet"}
                onValueChange={(v) => onCategoryChange(item!.id, v)}
              >
                <SelectTrigger className="h-9 text-sm rounded-lg w-auto min-w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.emoji} {c.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Badge variant="secondary" className="text-xs">
                {getCategoryEmoji(item!.category)}{" "}
                {item!.category ?? "Ikke klassifisert"}
              </Badge>
            )}
          </div>

          {item!.status === "done" && item!.improvement_note && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {isParent ? "Refleksjon" : "Hva kan jeg gjøre lettere neste gang?"}
              </p>
              <p className="text-sm bg-muted rounded-lg p-2">{item!.improvement_note}</p>
            </div>
          )}

          {/* Approval banner */}
          {needsApproval && isParent && (
            <div className="rounded-lg border border-amber-400/50 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Denne oppgaven er lagt inn av {creatorName || "den andre forelderen"} og trenger din godkjenning før barnet ser den.
              </p>
              <div className="flex gap-2">
                {onApprove && (
                  <Button size="sm" className="flex-1" onClick={() => onApprove(item!.id)}>
                    Godkjenn
                  </Button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive" className="flex-1">
                      Slett
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Slette foreslått oppgave?</AlertDialogTitle>
                      <AlertDialogDescription>
                        «{item!.title}» blir slettet permanent.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Avbryt</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onDelete(item!.id)}>
                        Slett
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {isParent && item!.status !== "done" && !isEditing && (
              <Button variant="outline" className="flex-1" onClick={startEditing}>
                Rediger
              </Button>
            )}

            {/* Reset button for parent on active items */}
            {isParent && item!.status === "active" && !isEditing && onReset && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="flex-1">
                    Nullstill
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Nullstille oppgaven?</AlertDialogTitle>
                    <AlertDialogDescription>
                      «{item!.title}» settes tilbake til «Ikke startet».
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Avbryt</AlertDialogCancel>
                    <AlertDialogAction onClick={() => onReset(item!.id)}>
                      Nullstill
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {isParent && isEditing && (
              <>
                <Button className="flex-1" onClick={saveEdit} disabled={!editTitle.trim()}>
                  Lagre
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setIsEditing(false)}>
                  Avbryt
                </Button>
              </>
            )}

            {!isEditing && item!.status !== "done" && (
              <Button className="flex-1" onClick={() => onComplete(item!.id)}>
                {isParent ? "Marker som ferdig" : "Jeg er ferdig! 🎉"}
              </Button>
            )}

            {isParent && !isEditing && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="flex-1">
                    Slett
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Slette oppgave?</AlertDialogTitle>
                    <AlertDialogDescription>
                      «{item!.title}» blir slettet permanent.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Avbryt</AlertDialogCancel>
                    <AlertDialogAction onClick={() => onDelete(item!.id)}>
                      Slett
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>

          {/* Move to another day */}
          {isParent && item!.status !== "done" && !isEditing && onMove && (
            <div className="pt-2">
              <p className="text-xs text-muted-foreground mb-1.5">Flytt til annen dag</p>
              <div className="flex gap-1.5 flex-wrap">
                {[1, 2, 3, 4, 5, 6, 7].map((offset) => {
                  const d = new Date();
                  d.setDate(d.getDate() + offset);
                  const dayLabel = d.toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" });
                  const iso = d.toISOString().split("T")[0];
                  return (
                    <Button
                      key={offset}
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 px-2"
                      onClick={() => onMove(item!.id, iso)}
                    >
                      {dayLabel}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
